import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { cn, Icon } from '@lqa-frontend/ui-system';
import { DropdownMenu } from '../dropdown/Dropdown';
import { Button } from '../buttons/Button';
import { BodyM, BodyMSemiBold, BodyS } from '../typography';
import {
	LoadingAnimation,
	LoadingAnimationSize,
} from '../loader/LoadingAnimation';
import { Skeleton } from '../skeleton/Skeleton';
import { FixedSizeList } from 'react-window';
import type { ListChildComponentProps } from 'react-window';
import { SearchClearableSelectDropdown } from './components/search-clearable-select/SearchClearableSelectDropdown';

export interface IDropdownItem {
	id: string | number;
	name: string;
}

export enum SelectDropdownVariant {
	Select = 'Select',
	SelectClearable = 'SelectClearable',
	SearchClearable = 'SearchClearable',
	MultiSelect = 'MultiSelect',
}

export enum SelectDropdownLoadingVariant {
	Default = 'default',
	Spinner = 'spinner',
}

interface ISelectDropdownSharedProps<T extends IDropdownItem> {
	items: T[];
	triggerIcon?: React.ReactNode;
	triggerClassName?: string;
	contentClassName?: string;
	hasSameWidth?: boolean;
	placeholder?: string;
	/** When true, placeholder text uses zinc-400 instead of the default text color. */
	hasGreyPlaceholder?: boolean;
	leadingIcon?: React.ReactNode;
	align?: React.ComponentProps<typeof DropdownMenu.Content>['align'];
	TEST_ID: string;
	onOpen?: (close: () => void) => void;
	isLoadingOptions?: boolean;
	/**
	 * When true, the trigger cannot open the menu. Distinct from
	 * `isLoadingOptions`: the label stays visible and the control looks disabled.
	 */
	isDisabled?: boolean;
	/**
	 * Trigger loading treatment when `isLoadingOptions` is true.
	 * `default` keeps the skeleton trigger; `spinner` centers a loading icon.
	 */
	loadingVariant?: SelectDropdownLoadingVariant;
	isSmall?: boolean;
	menuPortalContainer?: HTMLElement | null;
}

export interface ISingleSelectDropdownProps<
	T extends IDropdownItem,
> extends ISelectDropdownSharedProps<T> {
	selectedItem: T | null;
	onSelect: (item: T | null) => void;
	variant:
		| SelectDropdownVariant.Select
		| SelectDropdownVariant.SelectClearable
		| SelectDropdownVariant.SearchClearable;
	isVirtualized?: boolean;
	virtualListMaxHeight?: number;
	virtualListRowHeight?: number;
	onSearchTermChange?: (term: string) => void;
	searchDebounceMs?: number;
	totalItems?: number;
}

export interface IMultiSelectDropdownProps<
	T extends IDropdownItem,
> extends ISelectDropdownSharedProps<T> {
	selectedItems: T[];
	onSelectedItemsChange: (items: T[]) => void;
	variant: SelectDropdownVariant.MultiSelect;
}

export type ISelectDropdownProps<T extends IDropdownItem> =
	ISingleSelectDropdownProps<T> | IMultiSelectDropdownProps<T>;

interface ISelectDropdownVirtualListRowData<T extends IDropdownItem> {
	displayedItems: T[];
	selectedItem: T | null;
	setTestId: (elementName: string) => string;
	onPick: (item: T) => void;
}

function SelectDropdownVirtualListRow<T extends IDropdownItem>({
	index,
	style,
	data,
}: ListChildComponentProps<ISelectDropdownVirtualListRowData<T>>) {
	const item = data.displayedItems[index];
	if (!item) {
		return <div style={style} />;
	}

	const isSelected = item.id === data.selectedItem?.id;
	const OptionLabel = isSelected ? BodyMSemiBold : BodyM;

	return (
		<div style={style} className="w-full min-w-0 overflow-hidden">
			<DropdownMenu.Item
				onSelect={() => data.onPick(item)}
				className="flex h-full w-full min-w-0 items-center gap-2 overflow-hidden rounded-s"
				data-testid={data.setTestId('dropdown-list-item')}
			>
				<OptionLabel className="min-w-0 w-full">
					<span className="inline-flex max-w-full min-w-0 items-center gap-8">
						<span
							className="min-w-0 truncate"
							data-testid={data.setTestId('dropdown-list-item-label')}
						>
							{item.name}
						</span>
						{isSelected ? (
							<span className="inline-flex shrink-0 items-center">
								<Icon name="check" size="xs" />
							</span>
						) : null}
					</span>
				</OptionLabel>
			</DropdownMenu.Item>
		</div>
	);
}

const makeDataTestId = (scope: string) => (elementName: string) =>
	`${scope}-${elementName}`;

enum ContentTypeToRender {
	Loading,
	Empty,
	NoMatches,
	Ready,
}

function getContentTypeToRender(
	isLoadingOptions: boolean | undefined,
	itemCount: number
): ContentTypeToRender {
	if (isLoadingOptions) {
		return ContentTypeToRender.Loading;
	}

	if (itemCount === 0) {
		return ContentTypeToRender.Empty;
	}

	return ContentTypeToRender.Ready;
}

function SelectDropdownPlainItemList<T extends IDropdownItem>({
	items,
	selectedItem,
	onSelect,
	setTestId,
}: Readonly<{
	items: T[];
	selectedItem: T | null;
	onSelect: (item: T) => void;
	setTestId: (elementName: string) => string;
}>) {
	return items.map((item) => {
		const isSelected = item.id === selectedItem?.id;
		const OptionLabel = isSelected ? BodyMSemiBold : BodyM;

		return (
			<DropdownMenu.Item
				key={item.id}
				onSelect={() => onSelect(item)}
				className="flex items-center gap-2"
				data-testid={setTestId('dropdown-list-item')}
			>
				<OptionLabel className="min-w-0 w-full">
					<span className="inline-flex max-w-full min-w-0 items-center gap-8">
						<span
							className="min-w-0 truncate"
							data-testid={setTestId('dropdown-list-item-label')}
						>
							{item.name}
						</span>
						{isSelected ? (
							<span className="inline-flex shrink-0 items-center">
								<Icon name="check" size="xs" />
							</span>
						) : null}
					</span>
				</OptionLabel>
			</DropdownMenu.Item>
		);
	});
}

function toggleSelectedItem<T extends IDropdownItem>(
	selectedItems: T[],
	item: T
): T[] {
	const isSelected = selectedItems.some(
		(selectedItem) => selectedItem.id === item.id
	);

	return isSelected
		? selectedItems.filter((selectedItem) => selectedItem.id !== item.id)
		: [...selectedItems, item];
}

function SelectDropdownCheckboxItemList<T extends IDropdownItem>({
	items,
	selectedItems,
	onSelectedItemsChange,
	setTestId,
}: Readonly<{
	items: T[];
	selectedItems: T[];
	onSelectedItemsChange: (items: T[]) => void;
	setTestId: (elementName: string) => string;
}>) {
	const selectedIds = new Set(selectedItems.map((item) => item.id));

	return items.map((item) => (
		<DropdownMenu.CheckboxItem
			key={item.id}
			checked={selectedIds.has(item.id)}
			onSelect={(event) => event.preventDefault()}
			onCheckedChange={() =>
				onSelectedItemsChange(toggleSelectedItem(selectedItems, item))
			}
			data-testid={setTestId('dropdown-checkbox-item')}
		>
			{item.name}
		</DropdownMenu.CheckboxItem>
	));
}

/**
 * Renders the menu options for whichever half of the props union is active.
 * Keeping the discriminant check here means the parent never has to branch on
 * `variant` to reach `onSelect` / `onSelectedItemsChange`.
 */
function SelectDropdownOptionList<T extends IDropdownItem>({
	selectProps,
	setTestId,
}: Readonly<{
	selectProps: ISelectDropdownProps<T>;
	setTestId: (elementName: string) => string;
}>) {
	if (selectProps.variant === SelectDropdownVariant.MultiSelect) {
		return (
			<SelectDropdownCheckboxItemList
				items={selectProps.items}
				selectedItems={selectProps.selectedItems}
				onSelectedItemsChange={selectProps.onSelectedItemsChange}
				setTestId={setTestId}
			/>
		);
	}

	return (
		<SelectDropdownPlainItemList
			items={selectProps.items}
			selectedItem={selectProps.selectedItem}
			onSelect={selectProps.onSelect}
			setTestId={setTestId}
		/>
	);
}

function SelectDropdownMenuBody<T extends IDropdownItem>({
	menuContentKind,
	isVirtualized,
	displayedItems,
	plainItemList,
	virtualListHeight,
	virtualListRowHeight,
	virtualListItemData,
}: Readonly<{
	menuContentKind: ContentTypeToRender;
	isVirtualized: boolean;
	displayedItems: T[];
	plainItemList: React.ReactNode;
	virtualListHeight: number;
	virtualListRowHeight: number;
	virtualListItemData: ISelectDropdownVirtualListRowData<T>;
}>) {
	return (
		<>
			{menuContentKind === ContentTypeToRender.Loading && (
				<div className="flex w-full justify-center gap-12 py-12">
					<LoadingAnimation size={LoadingAnimationSize.Small} />
					<BodyS className="text-text-secondary">Loading options</BodyS>
				</div>
			)}

			{menuContentKind === ContentTypeToRender.Empty && (
				<div className="flex justify-center p-10">
					<BodyM className="text-zinc-500">No data</BodyM>
				</div>
			)}

			{menuContentKind === ContentTypeToRender.Ready &&
				(isVirtualized ? (
					<FixedSizeList<ISelectDropdownVirtualListRowData<T>>
						height={virtualListHeight}
						width="100%"
						itemCount={displayedItems.length}
						itemSize={virtualListRowHeight}
						itemData={virtualListItemData}
					>
						{SelectDropdownVirtualListRow}
					</FixedSizeList>
				) : (
					plainItemList
				))}
		</>
	);
}

function useSelectDropdownOpenState(
	isInteractionBlocked: boolean | undefined,
	onOpen: ((close: () => void) => void) | undefined
) {
	const [isOpen, setOpen] = useState<boolean>(false);

	const handleOpenChange = useCallback(
		(isMenuOpen: boolean) => {
			if (isMenuOpen && isInteractionBlocked) {
				return;
			}

			if (isMenuOpen && onOpen) {
				onOpen(() => setOpen(false));
			}
			setOpen(isMenuOpen);
		},
		[onOpen, isInteractionBlocked]
	);

	useEffect(() => {
		if (isInteractionBlocked && isOpen) {
			setOpen(false);
		}
	}, [isInteractionBlocked, isOpen]);

	return { isOpen, setOpen, handleOpenChange };
}

function SelectDropdownTriggerLoadingContent({
	loadingVariant,
	leadingIcon,
	loadingSpinner,
	setTestId,
}: Readonly<{
	loadingVariant: SelectDropdownLoadingVariant;
	leadingIcon: React.ReactNode;
	loadingSpinner: React.ReactNode;
	setTestId: (elementName: string) => string;
}>) {
	switch (loadingVariant) {
		case SelectDropdownLoadingVariant.Spinner:
			return loadingSpinner;
		case SelectDropdownLoadingVariant.Default:
			return (
				<>
					{leadingIcon ? (
						<span
							className="inline-flex shrink-0 items-center opacity-50"
							data-testid={setTestId('leading-icon')}
						>
							{leadingIcon}
						</span>
					) : null}
					<Skeleton
						className="h-16 min-w-0 flex-1"
						data-testid={setTestId('loading-skeleton')}
					/>
					<div className="flex shrink-0 items-center">
						<Icon
							name="chevronDown"
							size="s"
							className="shrink-0 text-zinc-300"
						/>
					</div>
				</>
			);
		default: {
			const unhandledLoadingVariant: never = loadingVariant;
			throw new Error(
				`Unhandled SelectDropdown loading variant: ${unhandledLoadingVariant}`
			);
		}
	}
}

interface IResolvedLoadingProps {
	loadingVariant: SelectDropdownLoadingVariant;
	loadingSpinner: React.ReactNode;
}

function getVirtualListHeight(
	isVirtualized: boolean,
	itemCount: number,
	virtualListMaxHeight: number,
	virtualListRowHeight: number
): number {
	if (!isVirtualized || itemCount === 0) {
		return 0;
	}

	return Math.min(virtualListMaxHeight, itemCount * virtualListRowHeight);
}

function getMultiSelectTriggerLabel<T extends IDropdownItem>(
	selectedItems: T[],
	placeholder: string
): string {
	if (selectedItems.length === 0) {
		return placeholder;
	}

	return selectedItems.map((item) => item.name).join(', ');
}

/**
 * The single- and multi-select halves of the props union answer the same
 * questions in different ways. Resolving them up front lets the component
 * render from one flat shape instead of branching on `variant` at every use.
 */
interface ISelectDropdownConfig<T extends IDropdownItem> {
	selectedItem: T | null;
	isVirtualized: boolean;
	virtualListMaxHeight: number;
	virtualListRowHeight: number;
	triggerLabel: string;
	isPlaceholderVisible: boolean;
	hasSelectionToClear: boolean;
	onPick: (item: T) => void;
	onClear: () => void;
}

function toSingleSelectConfig<T extends IDropdownItem>(
	props: ISingleSelectDropdownProps<T>,
	placeholder: string
): ISelectDropdownConfig<T> {
	const { selectedItem, onSelect, variant } = props;

	return {
		selectedItem,
		isVirtualized: props.isVirtualized ?? false,
		virtualListMaxHeight: props.virtualListMaxHeight ?? 240,
		virtualListRowHeight: props.virtualListRowHeight ?? 40,
		triggerLabel: selectedItem?.name ?? placeholder,
		isPlaceholderVisible: selectedItem == null,
		hasSelectionToClear:
			variant !== SelectDropdownVariant.Select && selectedItem != null,
		onPick: onSelect,
		onClear: () => onSelect(null),
	};
}

function toMultiSelectConfig<T extends IDropdownItem>(
	props: IMultiSelectDropdownProps<T>,
	placeholder: string
): ISelectDropdownConfig<T> {
	const { selectedItems, onSelectedItemsChange } = props;

	return {
		selectedItem: null,
		isVirtualized: false,
		virtualListMaxHeight: 240,
		virtualListRowHeight: 40,
		triggerLabel: getMultiSelectTriggerLabel(selectedItems, placeholder),
		isPlaceholderVisible: selectedItems.length === 0,
		hasSelectionToClear: selectedItems.length > 0,
		onPick: (item) =>
			onSelectedItemsChange(toggleSelectedItem(selectedItems, item)),
		onClear: () => onSelectedItemsChange([]),
	};
}

function toSelectDropdownConfig<T extends IDropdownItem>(
	props: ISelectDropdownProps<T>,
	placeholder: string
): ISelectDropdownConfig<T> {
	if (props.variant === SelectDropdownVariant.MultiSelect) {
		return toMultiSelectConfig(props, placeholder);
	}

	return toSingleSelectConfig(props, placeholder);
}

function getRootClassName({
	isInteractionBlocked,
	isDisabled,
	isLoadingOptions,
	triggerClassName,
}: Readonly<{
	isInteractionBlocked: boolean;
	isDisabled: boolean;
	isLoadingOptions: boolean | undefined;
	triggerClassName: string | undefined;
}>): string {
	return cn(
		'flex min-w-0 max-w-full items-stretch overflow-hidden rounded-m border border-zinc-200 bg-white text-text-primary shadow-sm',
		'transition-[color,background-color,border-color,box-shadow,ring]',
		'hover:not-focus-within:bg-base-2',
		'outline-none focus-within:ring-2 focus-within:ring-interactive focus-within:ring-offset-0',
		isInteractionBlocked &&
			'pointer-events-none cursor-not-allowed hover:bg-white',
		isDisabled && !isLoadingOptions && 'opacity-50',
		triggerClassName
	);
}

function getTriggerClassName({
	isLoadingOptions,
	isSpinnerLoading,
}: Readonly<{
	isLoadingOptions: boolean | undefined;
	isSpinnerLoading: boolean;
}>): string {
	return cn(
		'w-full min-w-0 max-w-full shrink overflow-hidden whitespace-normal gap-8 rounded-none border-0 bg-transparent shadow-none hover:bg-transparent active:bg-transparent focus-visible:outline-none',
		isLoadingOptions && 'cursor-not-allowed',
		isSpinnerLoading ? 'justify-center' : 'justify-start'
	);
}

function getMenuContentClassName({
	hasSameWidth,
	isVirtualized,
	contentClassName,
}: Readonly<{
	hasSameWidth: boolean;
	isVirtualized: boolean;
	contentClassName: string | undefined;
}>): string {
	return cn(
		'p-0 z-[100]',
		hasSameWidth
			? 'w-[var(--radix-dropdown-menu-trigger-width)] min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)]'
			: null,
		'min-w-200',
		!isVirtualized &&
			'max-h-[min(280px,var(--radix-dropdown-menu-content-available-height,280px))] overflow-y-auto',
		contentClassName
	);
}

function SelectDropdownTriggerValueContent({
	leadingIcon,
	triggerLabel,
	hasGreyPlaceholder,
	isPlaceholderVisible,
	isClearControlVisible,
	isOpen,
	setTestId,
}: Readonly<{
	leadingIcon: React.ReactNode;
	triggerLabel: string;
	hasGreyPlaceholder: boolean;
	isPlaceholderVisible: boolean;
	isClearControlVisible: boolean;
	isOpen: boolean;
	setTestId: (elementName: string) => string;
}>) {
	return (
		<>
			{leadingIcon ? (
				<span
					className="inline-flex shrink-0 items-center"
					data-testid={setTestId('leading-icon')}
				>
					{leadingIcon}
				</span>
			) : null}
			<span
				className={cn(
					'min-w-0 flex-1 overflow-hidden text-left',
					// Button sets font-semibold; the grey placeholder must read as 400.
					hasGreyPlaceholder &&
						isPlaceholderVisible &&
						'font-normal text-zinc-400',
					isClearControlVisible && 'pr-36'
				)}
				data-testid={setTestId('selected-item-text')}
			>
				<span className="block truncate">{triggerLabel}</span>
			</span>
			<div className="flex shrink-0 items-center">
				<Icon
					name={isOpen ? 'chevronUp' : 'chevronDown'}
					size="s"
					className="shrink-0"
				/>
			</div>
		</>
	);
}

function SelectDropdownClearControl({
	onClear,
}: Readonly<{ onClear: () => void }>) {
	return (
		<div className="pointer-events-none absolute inset-y-0 right-28 z-10 flex items-center">
			<Button
				type="button"
				variant="link"
				className="pointer-events-auto h-36 w-36 shrink-0 rounded-none border-0 px-0 focus-visible:outline-none"
				data-testid="erase-option-button"
				aria-label="Clear selection"
				onClick={(event) => {
					event.stopPropagation();
					onClear();
				}}
			>
				<Icon name="x" size="xs" />
			</Button>
		</div>
	);
}

function RadixSelectDropdown<T extends IDropdownItem>(
	props: Readonly<ISelectDropdownProps<T> & IResolvedLoadingProps>
) {
	const {
		items,
		triggerIcon: _triggerIcon,
		triggerClassName,
		contentClassName,
		hasSameWidth = false,
		align = 'start',
		placeholder = 'Select',
		hasGreyPlaceholder = false,
		TEST_ID,
		onOpen,
		isLoadingOptions,
		isDisabled = false,
		loadingVariant,
		loadingSpinner,
		isSmall,
		menuPortalContainer,
		leadingIcon,
	} = props;
	const config = toSelectDropdownConfig(props, placeholder);
	const isInteractionBlocked = Boolean(isLoadingOptions) || isDisabled;
	const { isOpen, setOpen, handleOpenChange } = useSelectDropdownOpenState(
		isInteractionBlocked,
		onOpen
	);

	const setTestId = useMemo(() => makeDataTestId(TEST_ID), [TEST_ID]);

	const displayedItems = items;

	const virtualListHeight = getVirtualListHeight(
		config.isVirtualized,
		displayedItems.length,
		config.virtualListMaxHeight,
		config.virtualListRowHeight
	);

	const menuContentKind = getContentTypeToRender(
		isLoadingOptions,
		items.length
	);

	const isClearControlVisible =
		!isInteractionBlocked && config.hasSelectionToClear;

	const virtualListItemData: ISelectDropdownVirtualListRowData<T> = {
		displayedItems,
		selectedItem: config.selectedItem,
		setTestId,
		onPick: config.onPick,
	};

	const isSpinnerLoading = loadingSpinner != null;
	const handleClear = () => {
		config.onClear();
		setOpen(false);
	};
	const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
		event.currentTarget.focus();
	};

	return (
		<div
			className={getRootClassName({
				isInteractionBlocked,
				isDisabled,
				isLoadingOptions,
				triggerClassName,
			})}
			data-testid={setTestId('dropdown')}
			aria-busy={isLoadingOptions || undefined}
			aria-disabled={isDisabled || undefined}
		>
			<div className="relative min-w-0 flex-1">
				<DropdownMenu
					modal={false}
					open={isInteractionBlocked ? false : isOpen}
					onOpenChange={handleOpenChange}
				>
					<DropdownMenu.Trigger
						data-testid={setTestId('dropdown-menu-trigger')}
						asChild
					>
						{/* Trigger Button must stay a direct child of asChild (Radix Slot). */}
						<Button
							disabled={isInteractionBlocked}
							variant="outline"
							size={isSmall ? 's' : 'm'}
							className={getTriggerClassName({
								isLoadingOptions,
								isSpinnerLoading,
							})}
							onPointerDown={handlePointerDown}
						>
							{isLoadingOptions ? (
								<SelectDropdownTriggerLoadingContent
									loadingVariant={loadingVariant}
									leadingIcon={leadingIcon}
									loadingSpinner={loadingSpinner}
									setTestId={setTestId}
								/>
							) : (
								<SelectDropdownTriggerValueContent
									leadingIcon={leadingIcon}
									triggerLabel={config.triggerLabel}
									hasGreyPlaceholder={hasGreyPlaceholder}
									isPlaceholderVisible={config.isPlaceholderVisible}
									isClearControlVisible={isClearControlVisible}
									isOpen={isOpen}
									setTestId={setTestId}
								/>
							)}
						</Button>
					</DropdownMenu.Trigger>
					<DropdownMenu.Content
						portalContainer={menuPortalContainer}
						className={getMenuContentClassName({
							hasSameWidth,
							isVirtualized: config.isVirtualized,
							contentClassName,
						})}
						align={align}
						data-testid={setTestId('dropdown-menu-list')}
					>
						<SelectDropdownMenuBody
							menuContentKind={menuContentKind}
							isVirtualized={config.isVirtualized}
							displayedItems={displayedItems}
							plainItemList={
								<SelectDropdownOptionList
									selectProps={props}
									setTestId={setTestId}
								/>
							}
							virtualListHeight={virtualListHeight}
							virtualListRowHeight={config.virtualListRowHeight}
							virtualListItemData={virtualListItemData}
						/>
					</DropdownMenu.Content>
				</DropdownMenu>
				{isClearControlVisible ? (
					<SelectDropdownClearControl onClear={handleClear} />
				) : null}
			</div>
		</div>
	);
}

function SelectDropdown<T extends IDropdownItem>(
	props: Readonly<ISelectDropdownProps<T>>
) {
	const loadingVariant =
		props.loadingVariant ?? SelectDropdownLoadingVariant.Default;
	const isSpinnerLoading =
		Boolean(props.isLoadingOptions) &&
		loadingVariant === SelectDropdownLoadingVariant.Spinner;
	const setTestId = makeDataTestId(props.TEST_ID);
	const loadingSpinner = isSpinnerLoading ? (
		<Icon
			name="loading"
			size="s"
			className="animate-spin"
			aria-label="Loading"
			data-testid={setTestId('loading-spinner')}
		/>
	) : null;
	switch (props.variant) {
		case SelectDropdownVariant.SearchClearable:
			return (
				<SearchClearableSelectDropdown
					{...props}
					loadingVariant={loadingVariant}
					loadingSpinner={loadingSpinner}
				/>
			);
		case SelectDropdownVariant.Select:
		case SelectDropdownVariant.SelectClearable:
		case SelectDropdownVariant.MultiSelect:
			return (
				<RadixSelectDropdown
					{...props}
					loadingVariant={loadingVariant}
					loadingSpinner={loadingSpinner}
				/>
			);
		default: {
			const unhandledVariant: never = props;
			throw new Error(
				`Unhandled SelectDropdown variant: ${JSON.stringify(unhandledVariant)}`
			);
		}
	}
}

export { SelectDropdown };
